import {useUserMobileAppRedirectQuery} from "@/api/userApi";
import usePlanFlow from "@/hooks/usePlanFlow";
import {
  useAgencyId,
  useIsAdmin,
  useIsAgency,
  useIsCompany,
  useIsInfuencer,
  useSelectedCompany,
} from "@/hooks/useUserHooks";
import AuthLayout from "@/routing/AuthLayout";
import {locationAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {useAtomValue} from "jotai";
import {useEffect, useMemo, type FC} from "react";
import {Navigate, Outlet, useLocation} from "react-router";
import {useLocalStorage} from "react-use";

const RedirectIfLoggedIn: FC = () => {
  const location = useLocation();
  const loc = useAtomValue(locationAtom);
  const flowState = usePlanFlow();
  const [hasSeenNewPlansModel, setHasSeenNewPlansModel] = useLocalStorage<boolean>("hasSeenNewPlansModel", false, {
    raw: true,
  });
  const state = location.state || {from: {pathname: ""}};
  const user = useUserStore(state => state.user);
  const selectedCompany = useSelectedCompany();
  const isAdmin = useIsAdmin();
  const isAgency = useIsAgency();
  const isCompany = useIsCompany({excludeAdmins: true});
  const isInfluencer = useIsInfuencer({excludeAdmins: true});
  const agencyId = useAgencyId();

  const {data} = useUserMobileAppRedirectQuery({
    enabled: isInfluencer && user.influenced_admin === false,
  });

  useEffect(() => {
    if (data?.redirect) window.open(data.redirect, "_self");
  }, [data]);

  const navigationTarget = useMemo<string>(() => {
    if (state.from?.pathname) {
      return state.from.pathname;
    }

    if (!!loc.previousRoute && loc.previousRoute !== loc.currentRoute && loc.currentRoute === "/login") {
      return loc.previousRoute;
    }

    if (isAgency) {
      return `/agency/${agencyId}/dashboard/`;
    }

    if (isCompany) {
      if (
        flowState === "NO_PLAN_NO_CAMPAIGN" ||
        (flowState === "NO_PLAN_HAS_CAMPAIGN" && !hasSeenNewPlansModel) ||
        flowState === "HAS_PLAN_NO_CAMPAIGN" ||
        flowState === "LOADING"
      ) {
        if (flowState === "NO_PLAN_HAS_CAMPAIGN") {
          setHasSeenNewPlansModel(true);
        }

        return "/welcome/";
      } else {
        return `/company/${selectedCompany?.id}/dashboard/`;
      }
    } else {
      return "/campaigns/";
    }
  }, [
    user,
    flowState,
    hasSeenNewPlansModel,
    state.from?.pathname,
    loc.currentRoute,
    loc.previousRoute,
    isAgency,
    isCompany,
    isAdmin,
    agencyId,
    selectedCompany?.id,
  ]);

  if ((user.id && !isInfluencer) || user.influenced_admin) {
    return <Navigate to={navigationTarget} replace />;
  }
  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
};

export default RedirectIfLoggedIn;
